/**
 * Fullscreen image viewer for auth-protected images (blob-fetched).
 * Arrow keys / buttons navigate, Esc or backdrop closes. Pass the list of
 * file paths (without ?variant) and the starting index.
 */
import { useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { AuthImage } from '@/components/auth-image';

export function ImageLightbox({
  paths,
  index,
  on_close,
  on_navigate,
}: {
  paths: string[];
  index: number;
  on_close: () => void;
  on_navigate: (next: number) => void;
}) {
  const has_prev = index > 0;
  const has_next = index < paths.length - 1;

  const on_key = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') on_close();
      if (e.key === 'ArrowLeft' && has_prev) on_navigate(index - 1);
      if (e.key === 'ArrowRight' && has_next) on_navigate(index + 1);
    },
    [on_close, on_navigate, index, has_prev, has_next],
  );

  useEffect(() => {
    window.addEventListener('keydown', on_key);
    return () => window.removeEventListener('keydown', on_key);
  }, [on_key]);

  const path = paths[index];
  if (!path) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/85 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="image-lightbox"
      onClick={on_close}
    >
      <div className="flex items-center justify-between p-4 text-white" onClick={(e) => e.stopPropagation()}>
        <span className="text-sm text-white/80">
          {index + 1} / {paths.length}
        </span>
        <button
          type="button"
          onClick={on_close}
          aria-label="Close"
          data-testid="lightbox-close"
          className="rounded-full p-2 text-white hover:bg-white/10"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center p-4">
        {has_prev && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              on_navigate(index - 1);
            }}
            aria-label="Previous"
            data-testid="lightbox-prev"
            className="absolute left-4 z-10 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/25"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        <div onClick={(e) => e.stopPropagation()} className="flex max-h-full max-w-full items-center justify-center">
          <AuthImage
            key={path}
            path={`${path}?variant=full`}
            alt="Vendor photo"
            className="max-h-[80vh] max-w-full rounded-md object-contain"
          />
        </div>
        {has_next && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              on_navigate(index + 1);
            }}
            aria-label="Next"
            data-testid="lightbox-next"
            className="absolute right-4 z-10 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/25"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        )}
      </div>
    </div>
  );
}
